/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { useState } from 'react';
import { ChevronLeft, ChevronRight, Menu } from 'lucide-react';
import { SheetContent, SheetTrigger, Sheet } from '../UI/Sheet';
import { Button } from '../UI/Button';
import { SidebarItem, SidebarItemProps } from './SidebarItem';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
type SidebarProps = {
  title: string;
  topItems: Omit<SidebarItemProps, 'isExpanded'>[];
  bottomItems: Omit<SidebarItemProps, 'isExpanded'>[];
};

function Sidebar({ title, topItems, bottomItems }: SidebarProps) {
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);

  const toggleSidebar = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <>
      <Sheet open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
        <SheetTrigger asChild>
          <Button
            variant="ghost"
            className="md:hidden absolute top-4 left-4 z-50"
          >
            <Menu className="h-6 w-6" />
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-full bg-background-1 text-white">
          <h2 className="text-2xl font-bold text-white mb-4">{title}</h2>
          <nav>
            <ul className="space-y-2">
              {topItems.map((item, index) => (
                <li key={index}>
                  <SidebarItem
                    {...item}
                    isSelected={item.isSelected}
                    isExpanded={true}
                  />
                </li>
              ))}
              {bottomItems.map((item, index) => (
                <li key={index}>
                  <SidebarItem {...item} isExpanded={true} />
                </li>
              ))}
            </ul>
          </nav>
        </SheetContent>
      </Sheet>

      <aside
        className={`hidden md:flex flex-col bg-background-1 text-white transition-all duration-300 ease-in-out ${isExpanded ? 'w-64' : 'w-20'}`}
      >
        <div className="flex items-center justify-between p-4">
          <h2
            className={`text-2xl font-bold text-white ${isExpanded ? '' : 'hidden'}`}
          >
            {title}
          </h2>
          <button onClick={toggleSidebar} className="p-2">
            {isExpanded ? (
              <ChevronLeft className="h-6 w-6" />
            ) : (
              <ChevronRight className="h-6 w-6" />
            )}
          </button>
        </div>

        <nav className="flex-1">
          <ul className="space-y-2 p-4">
            {topItems.map((item, index) => (
              <li key={index}>
                <SidebarItem
                  icon={item.icon}
                  label={item.label}
                  isExpanded={isExpanded}
                  onClick={item.onClick}
                  isSelected={item.isSelected}
                />
              </li>
            ))}
          </ul>
        </nav>
        <div className="mt-auto p-4">
          {bottomItems.map((item, index) => (
            <SidebarItem
              isSelected={item.isSelected}
              key={index}
              icon={item.icon}
              label={item.label}
              isExpanded={isExpanded}
              onClick={item.onClick}
            />
          ))}
        </div>
      </aside>
    </>
  );
}

export { Sidebar };
